import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/ConfirmationModal';

const CreateRole = () => {
  const [role, setRole] = useState('');
  const [permissions, setPermissions] = useState([]); // All permissions grouped
  const [stats, setStats] = useState([]); // Stats permissions
  const [selected, setSelected] = useState([]); // Selected permissions
  const [selectedStats, setSelectedStats] = useState([]); // Selected stats permissions
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await apiGet('/api/admin/roles/permissions', {});
        setPermissions(res.data.data || []);
        setStats(res.data.stats || []);
      } catch (err) {
        toast.error('Failed to fetch permissions');
      }
    };
    fetchPermissions();
  }, []);

  const handlePermissionToggle = (perm) => {
    setSelected((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleStatsToggle = (stat) => {
    setSelectedStats((prev) =>
      prev.includes(stat) ? prev.filter((s) => s !== stat) : [...prev, stat]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) return toast.error('Role name is required');
    setShowConfirmModal(true);
  };

  const confirmCreateRole = async () => {
    setLoading(true);
    try {
      await apiPost('/api/admin/roles/create', {
        role,
        permissions: selected,
        dashboard_permissions: selectedStats,
      });
      toast.success('Role created successfully');
      setShowConfirmModal(false);
      navigate('/roles');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  // Combine permissions and stats for tab display
  const allGroups = [
    ...permissions,
    ...(stats.length > 0 ? [{ group: 'stats', permissions: Object.fromEntries(stats.map(stat => [stat, stat])) }] : [])
  ];

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Management
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground mb-6">Create <span className="text-gradient-ginox">Role</span></h1>

      <form onSubmit={handleSubmit}>
        {/* Role Name */}
        <div className="p-5 mb-6">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Role Name
            </label>
            <input
              type="text"
              value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              placeholder="Enter role name"
              required
            />
          </div>
        </div>

        {/* Permissions */}
        <div className="p-5 mb-6">
          <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
            Permissions
          </label>

          {/* Tab buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            {allGroups.map((group, idx) => (
              <button
                type="button"
                key={group.group}
                onClick={() => setActiveTab(idx)}
                className={
                  activeTab === idx
                    ? 'rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1 text-xs font-medium text-ginox-green transition-colors'
                    : 'rounded-md border border-border/70 bg-white/[0.02] px-3 py-1 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors'
                }
              >
                {group.group}
              </button>
            ))}
          </div>

          {/* Permission checkboxes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {allGroups[activeTab] && allGroups[activeTab].group === 'stats' ? (
              stats.map((stat) => (
                <label key={stat} className="flex items-center gap-2 cursor-pointer group/check">
                  <input
                    type="checkbox"
                    checked={selectedStats.includes(stat)}
                    onChange={() => handleStatsToggle(stat)}
                    className="h-3.5 w-3.5 rounded border border-border/70 bg-white/[0.03] accent-ginox-green"
                  />
                  <span className="text-sm text-muted-foreground group-hover/check:text-foreground transition-colors">{stat}</span>
                </label>
              ))
            ) : (
              allGroups[activeTab] && Object.entries(allGroups[activeTab].permissions).map(([label, perm]) => (
                <label key={perm} className="flex items-center gap-2 cursor-pointer group/check">
                  <input
                    type="checkbox"
                    checked={selected.includes(perm)}
                    onChange={() => handlePermissionToggle(perm)}
                    className="h-3.5 w-3.5 rounded border border-border/70 bg-white/[0.03] accent-ginox-green"
                  />
                  <span className="text-sm text-muted-foreground group-hover/check:text-foreground transition-colors">{label}</span>
                </label>
              ))
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Role'}
          </button>
          <button
            type="button"
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
            onClick={() => navigate('/roles')}
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Create Role Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmCreateRole}
        title="Create Role"
        message={`Are you sure you want to create role "${role}" with ${selected.length} permissions and ${selectedStats.length} dashboard permissions?`}
        confirmText="Create Role"
        loading={loading}
        loadingText="Creating..."
      />
    </div>
  );
};

export default CreateRole;
